import Page from '../../components/PageWrapper';
import Button from '../../components/form-control/Button';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DASHBOARD_BASE } from '../../resources/links';
import PopOnMe from '../../components/elements/poponme';
import useUserAccount from '../../hooks/userAccountHook';
import GTable from '../../components/elements/GTable';
import { ITableRowAction } from '../../components/elements/GTable/GTable';
import { capitalizeSentence, getFormattedDate, getPhoneNumberFromShortCodeFormat, getTrialEndDaysFromDate, removeUnderscoresAndCapitalize, removeUnderscoresAndDashes } from '../../utils/miscFunctions';
import { useQuery } from '@tanstack/react-query';
import usePartner from '../../hooks/partnerHook';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../states/slices/resourceSlice';
import { Ban, Building, CheckCircle, Handshake, Mail, MessageSquarePlus, MoveRight, Pencil, Percent, Phone, UserCheck, UserCog, UserPlus } from 'lucide-react';
import PolicyStatus from '../../components/elements/PolicyStatus';
import Avatar from '../../components/Avatar';
import { COMPANY_TYPES } from '../../resources/enums';
import { getEnumsFromState } from '../../states/slices/resourceSlice';
import { IEnum } from '../../resources/interfaces';

const TYPE_FILTER_ALL = 'ALL';


function Partners(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
  const [showPerPage, setShowPerPage] = useState<number>(25);
  const navigate = useNavigate();
  const { user } = useUserAccount();
  const page = currentPage;
  const [searchString, setSearchString] = useState<string>()
  const { inviteMember, editTeam, addNewCompany, companiesPaginationData, assignMemberToTeam, assignTeamToPartner, assignRelationshipManager, setCommissionPercentage, setStaffSupervisor, addTeamToSlackWorkspace, PartnerModal, fetchAllCompanies, updateCompanyStatus } = usePartner();
  const countries = useSelector(getSelectedCountry)
  const countryId = countries?.id ?? 1
  const pageRef = useRef<HTMLDivElement>(null)
  const [selectedCompanyType, setSelectedCompanyType] = useState<string>(TYPE_FILTER_ALL);
  const enums = useSelector(getEnumsFromState);

  const companyTypeOptions: IEnum[] = useMemo(
    () => (enums?.companyTypes ?? []).filter((t: IEnum) => t?.key !== 'INTERNAL'),
    [enums]
  );

  const typeFilterPayload = useMemo<COMPANY_TYPES[]>(
    () => (selectedCompanyType === TYPE_FILTER_ALL ? [] : [selectedCompanyType as COMPANY_TYPES]),
    [selectedCompanyType]
  );

  const { data: companies, isLoading: companiesLoading } = useQuery({
    queryKey: ['fetchAllCompanies', showPerPage, countryId, page, selectedCompanyType],
    queryFn: () => fetchAllCompanies(countryId, page, showPerPage, typeFilterPayload)
  });

  const matchesSearch = (company: any, query: string) => {
    if (!query) return true;
    const q = query.toLowerCase().trim();
    const typeTitle = companyTypeOptions.find((t) => t.key === company?.type)?.title?.toLowerCase() ?? '';
    const statusLabel = company?.active ? 'active' : 'inactive';
    return [
      company?.name,
      company?.type,
      typeTitle,
      statusLabel
    ].some((field) => field?.toString().toLowerCase().includes(q));
  };

  const companyFound = companies?.filter((company: any) => matchesSearch(company, searchString ?? ''));
  const companyData = searchString ? companyFound : companies;

  const getCompanyTableRowActions = (data: any): ITableRowAction[] => {
    const actions = [];
    actions.push();

    if (user.isOwner()) {
      actions.push(
        {
          label: 'Edit company information',
          icon: <Pencil className="h-4 w-4" />,
          callback: (team: any) => {
            if (team) editTeam(team);
          },
          data: data
        },
        {
          label: 'Invite member',
          icon: <UserPlus className="h-4 w-4" />,
          callback: (team: any) => {
            if (team) inviteMember(team, 'fetchAllCompanies');
          },
          data: data
        },
        {
          label: 'Move member to company',
          icon: <MoveRight className="h-4 w-4" />,
          callback: (team: any) => {
            if (team) assignMemberToTeam(team, "fetchAllCompanies");
          },
          data: data
        },
        {
          label: 'Set relationship manager',
          icon: <UserCheck className="h-4 w-4" />,
          callback: (team: any) => {
            assignRelationshipManager(team);
          },
          data: data
        },
        {
          label: 'Set commission rate',
          icon: <Percent className="h-4 w-4" />,
          callback: (team: any) => {
            setCommissionPercentage(team);
          },
          data: data
        },
        {
          label: 'Add to slack workspace',
          icon: <MessageSquarePlus className="h-4 w-4" />,
          callback: (team: any) => {
            addTeamToSlackWorkspace(team);
          },
          data: data
        },
        {
          label: 'Add to partner',
          icon: <Handshake className="h-4 w-4" />,
          callback: (team: any) => {
            assignTeamToPartner(team);
          },
          data: data
        },
        {
          label: 'Assign staff to supervisor',
          icon: <UserCog className="h-4 w-4" />,
          callback: async () => {
            try {
              setStaffSupervisor(data.id);
            } catch (error) { }
          },
          data: data
        },
        {
          label: data.active ? 'Deactivate' : 'Activate',
          icon: data.active ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />,
          callback: async () => {
            try {
              updateCompanyStatus(data.active, data.id)
            } catch (error) { }
          },
          data: data
        }
      );
    }

    return actions;
  };

  const getTrialEndDateFromAcceptanceDate = (acceptanceDate: Date) => {
    if (!acceptanceDate) return { days: '-', hours: '-', minutes: '-', seconds: '-' };

    const now = new Date().getTime();
    const trialEnd = new Date(acceptanceDate).getTime() + (30 * 24 * 60 * 60 * 1000);
    const difference = trialEnd - now;

    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    } else {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Company',
            accessor: 'name',
            Cell: (row: any) => {
              return (
                <div className='flex gap-2 items-center '>
                  <div className="w-10 h-10 bg-gray-300 dark:bg-darkBg-border rounded-full flex items-center justify-center">
                    {
                      row.row.original.logo ?
                        <Avatar src={row.row.original.logo} size="xs" /> :
                        <span className="text-sm font-medium text-gray-700 dark:text-darkBg-mainText">
                          {row.cell.value[0]}
                        </span>
                    }
                  </div>
                  {row.cell.value ? removeUnderscoresAndDashes(row.cell.value) : '-'}

                </div>
              );
            }
          },

          {
            Header: 'Contact',
            accessor: 'mobilePhone',
            Cell: (row: any) => {
              return (
                row.row.original.email || row.cell.value ?
                  <div className="px-6  whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-darkBg-mainText flex items-center">
                      <Mail className="h-4 w-4 mr-1 text-gray-400 dark:text-darkBg-subText" />
                      <span>{row.row.original.email ? row.row.original.email : '-'}</span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-darkBg-subText flex items-center mt-1">
                      <Phone className="h-4 w-4 mr-1 text-gray-400 dark:text-darkBg-subText" />
                      <span>{row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}</span>
                    </div>
                  </div> :
                  <div className='text-center'><span>-</span></div>
              );
            }
          },
          {
            Header: 'Type',
            accessor: 'type',
            Cell: (row: any) => {
              return (
                <PolicyStatus
                  status={
                    row.cell.value ? capitalizeSentence(row.cell.value.split('_').join(' ')) : '-'
                  }
                  classes="px-2 !w-fit "
                />
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'active',
            Cell: (row: any) => {
              return (
                <div className="">
                  {row.cell.value ? (
                    <div className="px-2 border text-xs border-[#0F7125] dark:border-green-400 flex w-fit items-center justify-center text-[#0F7125] dark:text-green-400 rounded-full ">
                      Active
                    </div>
                  ) : (
                    <div className=" px-2 border text-xs border-[#FBC02D] dark:border-yellow-400 flex w-fit items-center justify-center text-[#FBC02D] dark:text-yellow-400 rounded-full ">
                      <span>Inactive</span>
                    </div>
                  )}
                </div>
              )
            }
          },
          {
            Header: 'Users',
            accessor: 'numberOfMembers',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Branches',
            accessor: 'numberOfBranches',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Terms Accepted On',
            accessor: 'termsAndConditionsAcceptedDateAndTime',
            Cell: (row: any) => {
              return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
            }
          },
          {
            Header: 'Trial Days Left',
            accessor: '',
            Cell: (row: any) => {
              return <span>{row.row.original.termsAndConditionsAcceptedDateAndTime ? getTrialEndDaysFromDate(row.row.original.termsAndConditionsAcceptedDateAndTime).days : '-'}</span>;
            }
          },
          {
            Header: 'Created',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
            }
          },
          {
            id: 'Action',
            Cell: (row: any) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  <PopOnMe actions={getCompanyTableRowActions(row.row.original)}>
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </PopOnMe>
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: String(page) });
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToTop()

  }, [currentPage])

  return (
    <Page title="Groups">
      <div className="bg-white dark:bg-darkBg w-full p-5 min-h-full  rounded-lg shadow-md" ref={pageRef}>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-5">
          <div>
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
              <h1 className="text-lg md:text-xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 dark:from-darkBg-mainText dark:to-darkBg-subText">Companies & Institutions</h1>
            </div>
            <p className="text-gray-600 dark:text-darkBg-subText ml-4 pl-2 border-l-2 border-gray-200 dark:border-darkBg-border">Manage all your partner companies and their details</p>
          </div>

          <div className="flex flex-col-reverse md:flex-row md:items-end gap-5">
            {user?.isInternalOwner() && (
              <div className="flex items-center gap-2 pb-2">
                <label htmlFor="company-type-filter" className="text-sm text-gray-500">
                  Type
                </label>
                <select
                  id="company-type-filter"
                  value={selectedCompanyType}
                  onChange={(e) => setSelectedCompanyType(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value={TYPE_FILTER_ALL}>All types</option>
                  {companyTypeOptions.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <Button
              bgColor="primary"
              label={"Add New Company"}
              icon={<Building width="20px" height="20px" className="mr-2" />}
              onClick={addNewCompany}
            />
          </div>
        </div>


        <div className="overflow-x-auto mt-5">
          <GTable
            columns={columns}
            data={companyData || []}
            classes="h-fit"
            loading={companiesLoading}
            tableType="company"
            paginationData={companiesPaginationData}
            onPageChange={handlePageChange}
            showSearch={true}
            onSearch={(searchString: string) => setSearchString(searchString)}
            showPerPage={showPerPage}
            onShowPerPageChange={(page) => setShowPerPage(page)}
            onRowClick={(team) => {
              navigate(`${DASHBOARD_BASE}/teams/${team.id}`);
            }}
          />
        </div>
      </div>

      <PartnerModal />
    </Page>
  );
}

export default Partners;
